import React, { createContext, useContext, useState, useCallback, useRef } from "react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

const ConfirmContext = createContext(null);

export function ConfirmProvider({ children }) {
  const [open, setOpen] = useState(false);
  const [opts, setOpts] = useState({ title: "Bekreft", description: "", confirmLabel: "Bekreft", cancelLabel: "Avbryt", destructive: false });
  const resolveRef = useRef(null);

  const confirm = useCallback((options) => {
    setOpts({ title: "Bekreft", description: "", confirmLabel: "Bekreft", cancelLabel: "Avbryt", destructive: false, ...options });
    setOpen(true);
    return new Promise((resolve) => {
      resolveRef.current = resolve;
    });
  }, []);

  const handle = (result) => {
    setOpen(false);
    if (resolveRef.current) {
      resolveRef.current(result);
      resolveRef.current = null;
    }
  };

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <AlertDialog open={open} onOpenChange={(v) => !v && handle(false)}>
        <AlertDialogContent className="rounded-sm" data-testid="confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading uppercase tracking-tight">{opts.title}</AlertDialogTitle>
            {opts.description && <AlertDialogDescription>{opts.description}</AlertDialogDescription>}
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="confirm-cancel" onClick={() => handle(false)} className="rounded-sm">{opts.cancelLabel}</AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-ok"
              onClick={() => handle(true)}
              className={`rounded-sm font-heading uppercase tracking-widest ${opts.destructive ? "bg-rose-600 hover:bg-rose-700 text-white" : "bg-black hover:bg-neutral-800 text-white"}`}
            >
              {opts.confirmLabel}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ConfirmContext.Provider>
  );
}

export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error("useConfirm must be used inside <ConfirmProvider>");
  return ctx;
}
